import { useEffect, useState } from "react";
import { api, getAuth } from "@/lib/api";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";

const inputClass = "w-full mt-1 border border-neutral-300 px-3 py-2.5 text-sm focus:outline-none focus:border-black";

export default function TAdminProfile() {
  const [tenant, setTenant] = useState(null);
  const signInPhone = getAuth()?.user?.phone;
  const [form, setForm] = useState({ business_name: "", contact: { email: "", phone: "", whatsapp: "", address: "", city: "", state: "" } });

  // Booking time slots: saved as soon as one is added or removed.
  const [slots, setSlots] = useState([]);
  const [newSlot, setNewSlot] = useState({ from: "", to: "" });
  const [savingSlots, setSavingSlots] = useState(false);

  const saveSlots = async (next) => {
    setSavingSlots(true);
    try {
      const r = await api.put("/tenant/booking-slots", { slots: next });
      setSlots(r.data.slots);
      return true;
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not save the time slots");
      return false;
    } finally { setSavingSlots(false); }
  };
  const addSlot = async (e) => {
    e.preventDefault();
    if (!newSlot.from || !newSlot.to) { toast.error("Choose a start and an end time"); return; }
    if (newSlot.from >= newSlot.to) { toast.error("The end time must be after the start time"); return; }
    const slot = `${newSlot.from}-${newSlot.to}`;
    if (slots.includes(slot)) { toast.error("That time slot is already there"); return; }
    if (await saveSlots([...slots, slot])) { setNewSlot({ from: "", to: "" }); toast.success(`Added ${slot}`); }
  };
  const removeSlot = async (slot) => {
    if (slots.length === 1) { toast.error("Keep at least one time slot"); return; }
    if (await saveSlots(slots.filter((s) => s !== slot))) toast.success(`Removed ${slot}`);
  };

  useEffect(() => {
    api.get("/tenant/me").then((r) => {
      setTenant(r.data.tenant);
      setSlots(r.data.tenant?.booking_slots || []);
      setForm({
        business_name: r.data.tenant?.business_name || "",
        contact: { email: "", phone: "", whatsapp: "", address: "", city: "", state: "", ...(r.data.tenant?.contact || {}) },
      });
    });
  }, []);

  const save = async () => {
    try {
      await api.put("/tenant/profile", form);
      toast.success("Profile updated");
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not save"); }
  };

  if (!tenant) return <div className="text-sm text-neutral-500">Loading…</div>;

  return (
    <div data-testid="tadmin-profile">
      <div className="flex flex-wrap justify-between items-end gap-3 mb-6">
        <div><div className="admin-overline">Business</div><h1 className="admin-heading text-3xl font-bold mt-1">Profile</h1></div>
        <button onClick={save} className="bg-black text-white px-5 py-2.5 text-xs font-semibold uppercase tracking-wider min-h-[44px]" data-testid="tadmin-profile-save">Save</button>
      </div>

      <div className="bg-white border border-neutral-200 p-4 sm:p-6 grid md:grid-cols-2 gap-4">
        <div className="md:col-span-2"><label className="admin-overline">Business name</label>
          <input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className={inputClass} /></div>
        {["email", "phone", "whatsapp", "address", "city", "state"].map((k) => (
          <div key={k}><label className="admin-overline">{k}</label>
            <input value={form.contact[k] || ""} onChange={(e) => setForm({ ...form, contact: { ...form.contact, [k]: e.target.value } })} className={inputClass} /></div>
        ))}
      </div>

      <div className="bg-white border border-neutral-200 p-4 sm:p-6 mt-6" data-testid="tadmin-booking-slots">
        <div className="admin-overline mb-1">Booking time slots</div>
        <p className="text-sm text-neutral-600 max-w-2xl">The times your customers can choose when they book a service. Add or remove a slot and it is saved at once. Bookings already made keep their time.</p>
        <div className="flex flex-wrap gap-2 mt-4" data-testid="tadmin-booking-slots-list">
          {slots.map((slot) => (
            <span key={slot} className="inline-flex items-center gap-1 border border-neutral-300 pl-3 pr-1 text-sm font-mono min-h-[44px]" data-testid={`tadmin-slot-${slot}`}>
              {slot}
              <button type="button" onClick={() => removeSlot(slot)} disabled={savingSlots} aria-label={`Remove ${slot}`}
                className="h-9 w-9 inline-flex items-center justify-center text-neutral-500 hover:text-black disabled:opacity-40" data-testid={`tadmin-slot-remove-${slot}`}>
                <X className="h-4 w-4" />
              </button>
            </span>
          ))}
        </div>
        <form onSubmit={addSlot} className="flex flex-wrap items-end gap-3 mt-5" data-testid="tadmin-slot-add-form">
          <div className="min-w-0"><label className="admin-overline" htmlFor="slot-from">From</label>
            <input id="slot-from" type="time" value={newSlot.from} onChange={(e) => setNewSlot({ ...newSlot, from: e.target.value })} className={`${inputClass} min-h-[44px]`} data-testid="tadmin-slot-from" /></div>
          <div className="min-w-0"><label className="admin-overline" htmlFor="slot-to">To</label>
            <input id="slot-to" type="time" value={newSlot.to} onChange={(e) => setNewSlot({ ...newSlot, to: e.target.value })} className={`${inputClass} min-h-[44px]`} data-testid="tadmin-slot-to" /></div>
          <button type="submit" disabled={savingSlots} className="bg-black text-white px-5 text-xs font-semibold uppercase tracking-wider min-h-[44px] inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="tadmin-slot-add">
            <Plus className="h-4 w-4" /> Add slot
          </button>
        </form>
      </div>

      <div className="bg-white border border-neutral-200 p-4 sm:p-6 mt-6">
        <div className="admin-overline mb-3">Subscription</div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 text-sm">
          <div><div className="text-xs text-neutral-500">Plan</div><div className="font-bold uppercase">{tenant.plan_code}</div></div>
          <div><div className="text-xs text-neutral-500">Status</div><div className="font-bold uppercase">{tenant.subscription_status}</div></div>
          <div className="min-w-0"><div className="text-xs text-neutral-500">Slug</div><div className="font-mono break-anywhere">{tenant.slug}</div></div>
        </div>
      </div>

      <div className="bg-white border border-neutral-200 p-4 sm:p-6 mt-6" data-testid="tadmin-signin-info">
        <div className="admin-overline mb-3">Sign-in</div>
        <p className="text-sm text-neutral-700 max-w-2xl">
          You sign in with your mobile number{signInPhone ? <> <span className="font-mono font-semibold break-anywhere">{signInPhone}</span></> : ""} and
          a one-time code sent to it by SMS or WhatsApp. There is no password. To change the number, contact the platform administrator.
        </p>
      </div>
    </div>
  );
}
